import React, { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { ChevronDown, Filter, X, Search, Download, Check } from 'lucide-react';
import { KonumPaylas } from './KonumPaylas';
import 'maplibre-gl/dist/maplibre-gl.css';
import './istanbul3d.css';

const BUILDINGS_MINZOOM = 13;
const HOME = { center: [29.05, 41.01], zoom: 9.7, pitch: 50, bearing: -15 };

// CARTO raster altlık API anahtarı zorunlu hale geldi → anahtar gerektirmeyen OpenFreeMap 'dark' vektör stili
const BASE_STYLE = 'https://tiles.openfreemap.org/styles/dark';
const OMT_SOURCE = 'openmaptiles';

const trNorm = (s) => String(s || '').toLocaleLowerCase('tr');

/**
 * Aramalı çoklu seçim açılır listesi (tür / ilçe filtreleri için).
 * options: [{ key, label, count, hex? }]
 */
const CokluSecimDropdown = ({ testId, ozet, baslik, aramaYer, options, selected, onToggle, onSelectAll, onClear, altBilgi }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const gorunen = q ? options.filter((o) => trNorm(o.label).includes(trNorm(q))) : options;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-btn`}
        className={`flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-full border bg-[#0c192af2] transition-colors ${
          selected.length > 0 ? 'border-amber-500/60 text-amber-200' : 'border-[#22344a] text-[#dce8f5] hover:border-[#3b5372]'
        }`}
      >
        <Filter className="w-3.5 h-3.5 text-amber-400" />
        <span>{ozet}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 mt-1.5 w-[300px] rounded-xl border border-[#22344a] overflow-hidden shadow-2xl"
          style={{ background: '#0d1826fa', backdropFilter: 'blur(16px)' }}
          data-testid={`${testId}-panel`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#1b2c41]">
            <span className="text-[10.5px] uppercase tracking-widest text-[#7d93ab]">{baslik}</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onSelectAll(gorunen.map((o) => o.key))}
                className="text-[11px] px-2 py-0.5 rounded-md border border-[#22344a] text-[#a9bdd2] hover:text-white hover:border-[#3b5372]"
                data-testid={`${testId}-select-all`}
              >
                Hepsini Seç
              </button>
              {selected.length > 0 && (
                <button
                  onClick={onClear}
                  className="text-[11px] px-2 py-0.5 rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  data-testid={`${testId}-clear`}
                >
                  Temizle
                </button>
              )}
            </div>
          </div>

          <div className="px-2.5 py-2 border-b border-[#1b2c41]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#7d93ab] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={aramaYer}
                className="w-full bg-[#0a1626] border border-[#22344a] rounded-lg pl-8 pr-7 py-1.5 text-[12px] text-[#dce8f5] placeholder:text-[#5c748f] outline-none focus:border-[#3b5372]"
                data-testid={`${testId}-search`}
              />
              {q && (
                <button
                  onClick={() => setQ('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7d93ab] hover:text-white"
                  data-testid={`${testId}-search-clear`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[240px] overflow-y-auto i3d-scroll py-1">
            {gorunen.length === 0 && (
              <p className="text-[11.5px] text-[#7d93ab] text-center py-4 px-3">Aramaya uyan kayıt yok.</p>
            )}
            {gorunen.map((o) => {
              const secili = selected.includes(o.key);
              return (
                <button
                  key={o.key}
                  onClick={() => onToggle(o.key)}
                  data-testid={`${testId}-opt-${o.key}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[#16273c] transition-colors"
                >
                  <span
                    className={`w-4 h-4 rounded-[5px] border flex-none grid place-items-center ${secili ? 'border-transparent' : 'border-[#33517c]'}`}
                    style={secili ? { background: o.hex || '#f5a623' } : {}}
                  >
                    {secili && <Check className="w-3 h-3 text-gray-950" />}
                  </span>
                  {o.hex && <span className="w-2 h-2 rounded-full flex-none" style={{ background: o.hex, boxShadow: `0 0 7px ${o.hex}` }} />}
                  <span className="text-[12.5px] text-[#dce8f5] flex-1 min-w-0 truncate">{o.label}</span>
                  <span className={`text-[11px] flex-none ${o.count > 0 ? 'text-[#7d93ab]' : 'text-[#41556e]'}`}>{o.count}</span>
                </button>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-[#1b2c41] text-[11px] text-[#7d93ab]">{altBilgi}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Yeniden kullanılabilir 3D İstanbul haritası (MapLibre GL — gerçek OSM bina ekstrüzyonu).
 *
 * items: [{ id, name, district, lat, lon, approx, status, rows:[{label,value}], pct, note }]
 * statusMap: { statusKey: { label, hex } }
 */
export const Istanbul3DMap = ({
  items,
  statusMap,
  onOpenRecord,
  openRecordLabel = 'Kaydı Aç →',
  height = 620,
  testIdPrefix = 'i3d',
  groupFilterLabel = 'Tesis Türü',
  groupFilterLabelPlural = 'Tesis Türleri',
  exportTitle = 'Harita Kayıtları',
  exportFileName = 'harita-kayitlari',
}) => {
  const wrapRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const filtreKey = `i3d-filtre:${testIdPrefix}`;
  const kayitliFiltre = () => {
    try { return JSON.parse(localStorage.getItem(filtreKey) || 'null') || {}; } catch { return {}; }
  };
  const [aktifDurumlar, setAktifDurumlar] = useState(() => kayitliFiltre().durumlar || []); // boş = tümü
  const [aktifIlceler, setAktifIlceler] = useState(() => kayitliFiltre().ilceler || []); // boş = tümü
  const [aktifGruplar, setAktifGruplar] = useState(() => kayitliFiltre().gruplar || []); // boş = tümü
  const [indirOpen, setIndirOpen] = useState(false);
  const indirRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [listOpen, setListOpen] = useState(true);
  const [legendOpen, setLegendOpen] = useState(false);
  const [showZoomHint, setShowZoomHint] = useState(true);

  const selected = useMemo(
    () => items.find((i) => String(i.id) === String(selectedId)) || null,
    [items, selectedId]
  );
  const uygunMu = (i, atla) => (
    (atla === 'durum' || aktifDurumlar.length === 0 || aktifDurumlar.includes(i.status)) &&
    (atla === 'ilce' || aktifIlceler.length === 0 || aktifIlceler.includes(i.district)) &&
    (atla === 'grup' || aktifGruplar.length === 0 || aktifGruplar.includes(i.group || '—'))
  );

  const visibleItems = useMemo(
    () => items.filter((i) => uygunMu(i, null)),
    [items, aktifDurumlar, aktifIlceler, aktifGruplar]
  );
  const statusCounts = useMemo(() => {
    const c = {};
    items.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);
  const ilceCounts = useMemo(() => {
    const c = {};
    items.forEach((i) => { c[i.district || '—'] = (c[i.district || '—'] || 0) + 1; });
    return c;
  }, [items]);
  const grupCounts = useMemo(() => {
    const c = {};
    items.forEach((i) => { if (i.group !== undefined) c[i.group || '—'] = (c[i.group || '—'] || 0) + 1; });
    return c;
  }, [items]);
  // çapraz sayımlar: her filtrenin sayıları diğer filtreler uygulanmış haliyle gösterilir
  const caprazSayim = useMemo(() => {
    const durum = {}; const ilce = {}; const grup = {};
    items.forEach((i) => {
      if (uygunMu(i, 'durum')) durum[i.status] = (durum[i.status] || 0) + 1;
      if (uygunMu(i, 'ilce')) ilce[i.district || '—'] = (ilce[i.district || '—'] || 0) + 1;
      if (uygunMu(i, 'grup')) grup[i.group || '—'] = (grup[i.group || '—'] || 0) + 1;
    });
    return { durum, ilce, grup };
  }, [items, aktifDurumlar, aktifIlceler, aktifGruplar]);

  // ---------- harita kurulumu ----------
  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASE_STYLE,
      ...HOME,
      antialias: true,
      maxPitch: 72,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
    try {
      map.addControl(new maplibregl.FullscreenControl({ container: wrapRef.current }), 'bottom-right');
    } catch (e) { /* fullscreen desteklenmiyorsa geç */ }
    map.touchZoomRotate.enableRotation();

    map.on('load', () => {
      map.resize();
      try {
        // Deniz/kara ayrımını netleştiren su katmanı (koyu altlıkta kıyı çizgisi belirsiz kalıyor)
        map.addLayer({
          id: 'water-tint',
          type: 'fill',
          source: OMT_SOURCE,
          'source-layer': 'water',
          paint: { 'fill-color': '#0a2033', 'fill-opacity': 0.9, 'fill-outline-color': '#2a4c6e' },
        });
      } catch (e) { /* su katmanı eklenemedi — altlık yine çalışır */ }
      try {
        map.addLayer({
          id: '3d-buildings',
          type: 'fill-extrusion',
          source: OMT_SOURCE,
          'source-layer': 'building',
          minzoom: BUILDINGS_MINZOOM,
          paint: {
            'fill-extrusion-color': [
              'interpolate', ['linear'],
              ['coalesce', ['get', 'render_height'], 8],
              0, '#33485f',
              40, '#456081',
              120, '#5a7ba3',
            ],
            'fill-extrusion-height': [
              'interpolate', ['linear'], ['zoom'],
              BUILDINGS_MINZOOM, 0,
              BUILDINGS_MINZOOM + 1.2, ['coalesce', ['get', 'render_height'], 8],
            ],
            'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
            'fill-extrusion-opacity': 0.95,
          },
        });
      } catch (e) { /* bina katmanı eklenemedi — altlık yine çalışır */ }
    });

    const onZoom = () => {
      const z = map.getZoom();
      setShowZoomHint(z < BUILDINGS_MINZOOM - 0.2);
      if (wrapRef.current) wrapRef.current.classList.toggle('i3d-far', z < 10.8);
    };
    map.on('zoom', onZoom);
    onZoom();

    mapRef.current = map;
    return () => {
      Object.values(markersRef.current).forEach((m) => m.marker.remove());
      markersRef.current = {};
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const selectItem = (id, fly = true) => {
    setSelectedId(id);
    Object.entries(markersRef.current).forEach(([k, m]) =>
      m.el.classList.toggle('sel', String(k) === String(id))
    );
    const it = itemsRef.current.find((i) => String(i.id) === String(id));
    if (fly && it && mapRef.current) {
      mapRef.current.flyTo({
        center: [it.lon, it.lat],
        zoom: 16.2, pitch: 62, bearing: -25,
        duration: 1800, essential: true,
      });
    }
  };

  // ---------- pinler ----------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(markersRef.current).forEach((m) => m.marker.remove());
    markersRef.current = {};
    items.forEach((it) => {
      const st = statusMap[it.status] || { hex: '#7d93ab', label: it.status };
      const el = document.createElement('div');
      el.className = it.approx ? 'i3d-mk i3d-approx' : 'i3d-mk';
      if (it.approx) el.title = 'Yaklaşık konum (ilçe merkezi) — kayda koordinat girilmemiş';
      el.style.color = st.hex;
      el.innerHTML =
        '<span class="i3d-pulse"></span><span class="i3d-core"></span><span class="i3d-lbl"></span>';
      const name = it.name || '';
      el.querySelector('.i3d-lbl').textContent =
        name.length > 26 ? name.slice(0, 25) + '…' : name;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        selectItem(it.id, true);
      });
      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([it.lon, it.lat])
        .addTo(map);
      markersRef.current[it.id] = { marker, el, status: it.status, district: it.district, group: it.group };
    });
  }, [items, statusMap]);

  // ---------- çoklu tür/durum + ilçe filtresi ----------
  const turSecenekleri = useMemo(
    () => Object.entries(statusMap)
      .filter(([key]) => (statusCounts[key] || 0) > 0)
      .map(([key, s]) => ({ key, label: s.label, hex: s.hex, count: caprazSayim.durum[key] || 0 })),
    [statusMap, statusCounts, caprazSayim]
  );

  const ilceSecenekleri = useMemo(
    () => Object.keys(ilceCounts)
      .sort((a, b) => a.localeCompare(b, 'tr'))
      .map((d) => ({ key: d, label: d, count: caprazSayim.ilce[d] || 0 })),
    [ilceCounts, caprazSayim]
  );

  const grupSecenekleri = useMemo(
    () => Object.keys(grupCounts)
      .sort((a, b) => a.localeCompare(b, 'tr'))
      .map((g) => ({ key: g, label: g, count: caprazSayim.grup[g] || 0 })),
    [grupCounts, caprazSayim]
  );

  const durumToggle = (key) => setAktifDurumlar((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );
  const ilceToggle = (key) => setAktifIlceler((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );
  const grupToggle = (key) => setAktifGruplar((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );

  // statusMap değişince (ör. tesis türüne göre ayır) geçersiz seçimleri temizle
  useEffect(() => {
    if (items.length === 0) return; // veri henüz yüklenmediyse hafızadaki seçimi koru
    setAktifDurumlar((prev) => {
      const kalan = prev.filter((k) => statusMap[k]);
      return kalan.length === prev.length ? prev : kalan;
    });
  }, [statusMap, items.length]);

  // seçimleri hatırla
  useEffect(() => {
    try {
      localStorage.setItem(filtreKey, JSON.stringify({ durumlar: aktifDurumlar, ilceler: aktifIlceler, gruplar: aktifGruplar }));
    } catch { /* kota dolu */ }
  }, [aktifDurumlar, aktifIlceler, aktifGruplar, filtreKey]);

  // indirme menüsü dışına tıklayınca kapat
  useEffect(() => {
    if (!indirOpen) return undefined;
    const onDown = (e) => { if (indirRef.current && !indirRef.current.contains(e.target)) setIndirOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [indirOpen]);

  // ---------- filtreli dışa aktarma ----------
  const filtreOzeti = [
    `${visibleItems.length} / ${items.length} kayıt`,
    aktifDurumlar.length > 0 && `Kategori: ${aktifDurumlar.map((k) => statusMap[k]?.label || k).join(', ')}`,
    aktifGruplar.length > 0 && `${groupFilterLabel}: ${aktifGruplar.join(', ')}`,
    aktifIlceler.length > 0 && `İlçe: ${aktifIlceler.join(', ')}`,
  ].filter(Boolean).join(' · ');

  const tabloVerisi = () => {
    const satirEtiketleri = [];
    visibleItems.forEach((i) => (i.rows || []).forEach((r) => {
      if (!satirEtiketleri.includes(r.label)) satirEtiketleri.push(r.label);
    }));
    const yuzdeVar = visibleItems.some((i) => i.pct !== null && i.pct !== undefined);
    const headers = ['Ad', 'İlçe', 'Kategori', ...satirEtiketleri, ...(yuzdeVar ? ['İlerleme (%)'] : []), 'Enlem', 'Boylam', 'Konum'];
    const rows = visibleItems.map((i) => {
      const m = {};
      (i.rows || []).forEach((r) => { m[r.label] = r.value ?? ''; });
      return [
        i.name || '',
        i.district || '',
        statusMap[i.status]?.label || i.status,
        ...satirEtiketleri.map((l) => m[l] ?? ''),
        ...(yuzdeVar ? [i.pct ?? ''] : []),
        i.lat.toFixed(6),
        i.lon.toFixed(6),
        i.approx ? 'Yaklaşık (ilçe merkezi)' : 'Kayıtlı koordinat',
      ];
    });
    return { headers, rows };
  };

  const excelIndir = async () => {
    setIndirOpen(false);
    const XLSX = await import('xlsx');
    const { headers, rows } = tabloVerisi();
    const ws = XLSX.utils.aoa_to_sheet([[exportTitle], [filtreOzeti], [], headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Harita');
    XLSX.writeFile(wb, `${exportFileName}-filtreli.xlsx`);
  };

  const pdfIndir = async () => {
    setIndirOpen(false);
    const { exportTableToPDF } = await import('../../utils/html2pdfExport');
    const { headers, rows } = tabloVerisi();
    await exportTableToPDF(headers, rows, exportTitle, `${exportFileName}-filtreli.pdf`, {
      subtitle: filtreOzeti,
      pageSize: 'a4',
      orientation: 'landscape',
    });
  };

  // ---------- filtre → pin görünürlüğü ----------
  useEffect(() => {
    Object.values(markersRef.current).forEach((m) => {
      const durumUygun = aktifDurumlar.length === 0 || aktifDurumlar.includes(m.status);
      const ilceUygun = aktifIlceler.length === 0 || aktifIlceler.includes(m.district);
      const grupUygun = aktifGruplar.length === 0 || aktifGruplar.includes(m.group || '—');
      m.el.style.display = durumUygun && ilceUygun && grupUygun ? 'block' : 'none';
    });
  }, [aktifDurumlar, aktifIlceler, aktifGruplar, items]);

  const goto3D = () => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    let best = null;
    let bd = Infinity;
    visibleItems.forEach((f) => {
      const d = (f.lon - c.lng) ** 2 + (f.lat - c.lat) ** 2;
      if (d < bd) { bd = d; best = f; }
    });
    const t = best || { lon: 29.02, lat: 41.01 };
    map.flyTo({ center: [t.lon, t.lat], zoom: 16, pitch: 62, bearing: -25, duration: 2200, essential: true });
    if (best) selectItem(best.id, false);
  };

  const flyHome = () => {
    setSelectedId(null);
    Object.values(markersRef.current).forEach((m) => m.el.classList.remove('sel'));
    if (mapRef.current) mapRef.current.flyTo({ ...HOME, duration: 1600 });
  };

  const selStatus = selected ? (statusMap[selected.status] || { hex: '#7d93ab', label: selected.status }) : null;

  return (
    <div ref={wrapRef} className="i3d-wrap" style={{ height }} data-testid={`${testIdPrefix}-map-wrap`}>
      {/* NOT: maplibre-gl.css `.maplibregl-map{position:relative}` kuralı Tailwind'in
          `absolute` sınıfını ezdiği için konum inline style ile veriliyor. */}
      <div
        ref={containerRef}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%' }}
      />

      {/* ---------- üst filtre: aramalı çoklu tür + ilçe seçimi ---------- */}
      <div className="absolute top-0 left-0 right-0 z-30 px-3 pt-3 pb-6 pointer-events-none"
        style={{ background: 'linear-gradient(180deg,#060d16e6 0%,#060d16a0 55%,transparent 100%)' }}>
        <div className="inline-flex flex-wrap items-start gap-2 pointer-events-auto">
          <CokluSecimDropdown
            testId={`${testIdPrefix}-filter`}
            baslik="Kategoriye göre filtrele"
            aramaYer="Kategori ara… (ör. arsa)"
            ozet={aktifDurumlar.length === 0 ? `Tüm Kategoriler (${items.length})` : `${aktifDurumlar.length} kategori seçili (${visibleItems.length})`}
            options={turSecenekleri}
            selected={aktifDurumlar}
            onToggle={durumToggle}
            onSelectAll={(keys) => setAktifDurumlar(keys)}
            onClear={() => setAktifDurumlar([])}
            altBilgi={<>Haritada <b className="text-[#dce8f5]">{visibleItems.length}</b> / {items.length} kayıt · seçim yapmazsanız tümü gösterilir</>}
          />
          {grupSecenekleri.length > 0 && (
            <CokluSecimDropdown
              testId={`${testIdPrefix}-grup`}
              baslik={`${groupFilterLabel}ne göre filtrele`}
              aramaYer={`${groupFilterLabel} ara… (ör. stad)`}
              ozet={aktifGruplar.length === 0 ? `Tüm ${groupFilterLabelPlural} (${grupSecenekleri.length})` : `${aktifGruplar.length} ${groupFilterLabel.toLocaleLowerCase('tr')} seçili (${visibleItems.length})`}
              options={grupSecenekleri}
              selected={aktifGruplar}
              onToggle={grupToggle}
              onSelectAll={(keys) => setAktifGruplar(keys)}
              onClear={() => setAktifGruplar([])}
              altBilgi={<>Renklendirme için kart başlığındaki "Tesis Türüne Göre Ayır" düğmesini kullanın</>}
            />
          )}
          <CokluSecimDropdown
            testId={`${testIdPrefix}-ilce`}
            baslik="İlçeye göre filtrele"
            aramaYer="İlçe ara… (ör. kadıköy)"
            ozet={aktifIlceler.length === 0 ? `Tüm İlçeler (${ilceSecenekleri.length})` : `${aktifIlceler.length} ilçe seçili (${visibleItems.length})`}
            options={ilceSecenekleri}
            selected={aktifIlceler}
            onToggle={ilceToggle}
            onSelectAll={(keys) => setAktifIlceler(keys)}
            onClear={() => setAktifIlceler([])}
            altBilgi={<>Tür ve ilçe filtreleri birlikte uygulanır</>}
          />

          <div className="relative" ref={indirRef}>
            <button
              onClick={() => setIndirOpen((o) => !o)}
              data-testid={`${testIdPrefix}-export-btn`}
              className="flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-full border border-[#22344a] bg-[#0c192af2] text-[#dce8f5] hover:border-[#3b5372] transition-colors"
              title="Haritada görünen kayıtları indir"
            >
              <Download className="w-3.5 h-3.5 text-emerald-400" />
              İndir ({visibleItems.length})
            </button>
            {indirOpen && (
              <div
                className="absolute left-0 mt-1.5 w-[220px] rounded-xl border border-[#22344a] overflow-hidden shadow-2xl"
                style={{ background: '#0d1826fa', backdropFilter: 'blur(16px)' }}
                data-testid={`${testIdPrefix}-export-menu`}
              >
                <div className="px-3 py-2 border-b border-[#1b2c41] text-[10.5px] uppercase tracking-widest text-[#7d93ab]">
                  Filtreli listeyi indir
                </div>
                <button
                  onClick={excelIndir}
                  disabled={visibleItems.length === 0}
                  className="w-full text-left px-3 py-2.5 text-[12.5px] text-[#dce8f5] hover:bg-[#16273c] disabled:opacity-40"
                  data-testid={`${testIdPrefix}-export-excel`}
                >
                  Excel (.xlsx)
                </button>
                <button
                  onClick={pdfIndir}
                  disabled={visibleItems.length === 0}
                  className="w-full text-left px-3 py-2.5 text-[12.5px] text-[#dce8f5] hover:bg-[#16273c] disabled:opacity-40 border-t border-[#1b2c41]"
                  data-testid={`${testIdPrefix}-export-pdf`}
                >
                  PDF (A4 yatay)
                </button>
                <div className="px-3 py-2 border-t border-[#1b2c41] text-[11px] text-[#7d93ab]">{filtreOzeti}</div>
              </div>
            )}
          </div>
        </div>

        {(aktifDurumlar.length > 0 || aktifIlceler.length > 0 || aktifGruplar.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-2 max-w-[640px] pointer-events-auto">
            {aktifDurumlar.map((key) => {
              const s = statusMap[key];
              if (!s) return null;
              return (
                <button
                  key={key}
                  onClick={() => durumToggle(key)}
                  data-testid={`${testIdPrefix}-filter-tag-${key}`}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full font-semibold text-gray-950"
                  style={{ background: s.hex }}
                  title="Kaldır"
                >
                  {s.label} ({caprazSayim.durum[key] || 0})
                  <X className="w-3 h-3" />
                </button>
              );
            })}
            {aktifGruplar.map((g) => (
              <button
                key={g}
                onClick={() => grupToggle(g)}
                data-testid={`${testIdPrefix}-grup-tag-${g}`}
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/15 text-emerald-200 hover:border-emerald-400"
                title="Kaldır"
              >
                {g} ({caprazSayim.grup[g] || 0})
                <X className="w-3 h-3" />
              </button>
            ))}
            {aktifIlceler.map((d) => (
              <button
                key={d}
                onClick={() => ilceToggle(d)}
                data-testid={`${testIdPrefix}-ilce-tag-${d}`}
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-[#3b5372] bg-[#16273c] text-[#dce8f5] hover:border-amber-500/60"
                title="Kaldır"
              >
                {d} ({caprazSayim.ilce[d] || 0})
                <X className="w-3 h-3" />
              </button>
            ))}
            <button
              onClick={() => { setAktifDurumlar([]); setAktifIlceler([]); setAktifGruplar([]); }}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid={`${testIdPrefix}-filters-reset`}
            >
              Tüm filtreleri temizle
            </button>
          </div>
        )}
      </div>

      {/* ---------- sol liste paneli ---------- */}
      {listOpen && (
        <div
          className={`absolute left-3 ${(aktifDurumlar.length > 0 || aktifIlceler.length > 0 || aktifGruplar.length > 0) ? 'top-24' : 'top-14'} bottom-3 z-20 w-64 flex flex-col overflow-hidden rounded-xl border border-[#22344a]`}
          style={{ background: '#0d1826f0', backdropFilter: 'blur(14px)' }}
          data-testid={`${testIdPrefix}-list-panel`}
        >
          <div className="px-3.5 py-2.5 border-b border-[#22344a] flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-[#7d93ab]">Kayıtlar</span>
            <span className="flex items-center gap-2">
              <b className="text-[11px] text-white">{visibleItems.length} / {items.length}</b>
              <button
                onClick={() => setListOpen(false)}
                className="text-[#7d93ab] hover:text-white text-sm px-1"
                data-testid={`${testIdPrefix}-list-close`}
              >✕</button>
            </span>
          </div>
          <div className="i3d-scroll overflow-y-auto flex-1 p-1.5">
            {visibleItems.length === 0 && (
              <p className="text-xs text-[#7d93ab] text-center py-6 px-3">
                Bu filtrede gösterilecek kayıt yok.
              </p>
            )}
            {visibleItems.map((f) => {
              const s = statusMap[f.status] || { hex: '#7d93ab', label: f.status };
              const isSel = String(selectedId) === String(f.id);
              return (
                <div
                  key={f.id}
                  onClick={() => selectItem(f.id, true)}
                  className={`px-2.5 py-2 rounded-lg cursor-pointer flex gap-2.5 items-start transition-colors ${
                    isSel ? 'bg-[#1a2e46] outline outline-1 outline-[#33517457]' : 'hover:bg-[#16273c]'
                  }`}
                  data-testid={`${testIdPrefix}-list-item`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-none mt-[5px]"
                    style={{ background: s.hex, boxShadow: `0 0 8px ${s.hex}` }}
                  />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold leading-tight text-[#dce8f5] break-words">{f.name}</div>
                    <div className="text-[11px] text-[#7d93ab] mt-0.5">
                      {f.district || '—'} · {s.label}{f.approx ? ' · ≈' : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!listOpen && (
        <button
          onClick={() => setListOpen(true)}
          className="absolute left-3 bottom-3 z-20 flex items-center gap-2 text-[12.5px] font-semibold text-[#dce8f5] rounded-full px-4 py-2 border border-[#22344a] shadow-lg"
          style={{ background: '#0d1826f0', backdropFilter: 'blur(12px)' }}
          data-testid={`${testIdPrefix}-list-open`}
        >
          ☰ Kayıtlar <b>({visibleItems.length})</b>
        </button>
      )}

      {/* ---------- sağ detay paneli ---------- */}
      {selected && (
        <div
          className="absolute right-3 top-14 z-30 w-[300px] max-h-[calc(100%-80px)] overflow-y-auto i3d-scroll rounded-xl border border-[#22344a] p-4"
          style={{ background: '#0d1826f0', backdropFilter: 'blur(14px)' }}
          data-testid={`${testIdPrefix}-detail-panel`}
        >
          <button
            onClick={flyHome}
            className="absolute top-2.5 right-3 w-6 h-6 grid place-items-center rounded-md text-[#7d93ab] hover:text-white hover:bg-[#1c3049] text-sm"
            data-testid={`${testIdPrefix}-detail-close`}
          >✕</button>
          <span
            className="inline-block text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-md mb-2.5"
            style={{ background: `${selStatus.hex}26`, color: selStatus.hex }}
          >
            {selStatus.label}
          </span>
          <h3 className="text-[15px] font-bold leading-snug text-[#dce8f5] pr-5 mb-0.5">{selected.name}</h3>
          <p className="text-xs text-[#7d93ab] mb-3">
            {selected.district || '—'} · {selected.lat.toFixed(4)}K, {selected.lon.toFixed(4)}D
            {selected.approx && <span className="text-amber-400/80"> · ≈ yaklaşık</span>}
          </p>
          {(selected.rows || []).map((r, idx) => (
            <div key={idx} className="flex justify-between gap-3 text-[12.5px] py-1.5 border-t border-[#1b2c41]">
              <span className="text-[#7d93ab] flex-none">{r.label}</span>
              <span className="font-semibold text-[#dce8f5] text-right break-words min-w-0">{r.value || '—'}</span>
            </div>
          ))}
          {selected.pct !== null && selected.pct !== undefined && (
            <>
              <div className="h-1.5 rounded bg-[#1b2c41] mt-2.5 mb-1 overflow-hidden">
                <div
                  className="h-full rounded transition-all duration-500"
                  style={{ width: `${selected.pct}%`, background: selStatus.hex }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-[#7d93ab] mb-2">
                <span>{selected.pctLabel || 'Fiziki gerçekleşme'}</span>
                <span>%{selected.pct}</span>
              </div>
            </>
          )}
          {selected.note && (
            <div className="text-xs text-[#a9bdd2] leading-relaxed bg-[#101f31] border border-[#1c2f47] rounded-lg px-3 py-2.5 mt-2">
              {selected.note}
            </div>
          )}
          {selected.approx && (
            <p className="text-[10.5px] text-amber-400/70 mt-2 leading-snug">
              📍 Koordinat girilmediği için ilçe merkezi baz alındı. Kayda enlem/boylam girildiğinde pin netleşir.
            </p>
          )}
          {onOpenRecord && (
            <button
              onClick={() => onOpenRecord(selected)}
              className="mt-3.5 w-full py-2.5 rounded-lg bg-amber-500 hover:brightness-110 text-gray-950 font-bold text-[13px]"
              data-testid={`${testIdPrefix}-open-record-btn`}
            >
              {openRecordLabel}
            </button>
          )}
          <KonumPaylas item={selected} testIdPrefix={testIdPrefix} />
        </div>
      )}

      {/* ---------- lejant ---------- */}
      <div
        className="absolute right-14 bottom-3 z-10 rounded-xl border border-[#22344a] px-3 py-2 max-w-[200px]"
        style={{ background: '#0d1826f0', backdropFilter: 'blur(14px)' }}
        data-testid={`${testIdPrefix}-legend`}
      >
        <div
          onClick={() => setLegendOpen(!legendOpen)}
          className="flex justify-between items-center gap-3 cursor-pointer text-[10.5px] uppercase tracking-widest text-[#7d93ab] select-none"
        >
          <b className="text-[#dce8f5]">Durum</b>
          <span>{legendOpen ? '▾' : '▸'}</span>
        </div>
        {legendOpen && (
          <div className="flex flex-col gap-1.5 mt-2">
            {Object.entries(statusMap).map(([key, s]) => (
              <div key={key} className="flex items-center gap-2 text-[11px] text-[#7d93ab]">
                <span className="w-2 h-2 rounded-full flex-none" style={{ background: s.hex, boxShadow: `0 0 7px ${s.hex}` }} />
                {s.label}
                <span className="ml-auto text-[#dce8f5] font-semibold">{statusCounts[key] || 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------- 3D zoom ipucu ---------- */}
      {showZoomHint && items.length > 0 && (
        <button
          onClick={goto3D}
          className="absolute left-1/2 -translate-x-1/2 bottom-12 z-20 flex items-center gap-2 text-xs font-semibold text-gray-950 bg-amber-500 rounded-full px-4 py-2 shadow-xl whitespace-nowrap"
          data-testid={`${testIdPrefix}-zoom-hint`}
        >
          🏢 3D binaları görmek için dokun
        </button>
      )}

      {/* ---------- kullanım ipucu ---------- */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 hidden md:block text-[11px] text-[#7d93ab] rounded-full px-4 py-1.5 border border-[#22344a] whitespace-nowrap"
        style={{ background: '#0c192acc', backdropFilter: 'blur(10px)' }}
      >
        Sürükle: kaydır · Sağ tık / Ctrl+sürükle: eğ ve döndür · Pin'e tıkla: detay
      </div>
    </div>
  );
};
